import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import '../styles/tsumugi-design.css';
import '../styles/pages/SharedRecipePage.css';

// API URL設定（環境変数から取得、フォールバックは本番URL）
const API_URL = process.env.REACT_APP_API_URL || 'https://hashimoto-backend01.onrender.com';

/**
 * SharedRecipePage - 共有レシピ専用ページ（認証不要）
 * つむぎデザインシステム準拠
 */
const SharedRecipePage = () => {
  const { shareId, recipeId } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // shareIdまたはrecipeIdのどちらかを使用
  const id = shareId || recipeId;

  useEffect(() => {
    document.title = 'レシピを見る | はしもと栄養パートナー';
    loadSharedRecipe();
  }, [id]);

  const loadSharedRecipe = async () => {
    try {
      setLoading(true);
      // shareIdの場合は/share/:id、recipeIdの場合は/api/recipes/:id/public（認証なし）
      const endpoint = shareId
        ? `${API_URL}/share/${shareId}`
        : `${API_URL}/api/recipes/${recipeId}/public`;

      const response = await fetch(endpoint);
      const data = await response.json();

      if (data.success) {
        setRecipe(data.recipe);
        document.title = `${data.recipe.title} | はしもと栄養パートナー`;
      } else {
        setError(data.message || 'レシピが見つかりませんでした');
      }
    } catch (err) {
      console.error('レシピ取得エラー:', err);
      setError('レシピの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;

    try {
      if (navigator.share) {
        await navigator.share({
          title: recipe.title,
          text: `「${recipe.title}」のレシピをチェック！`,
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert(`📋 共有URLをコピーしました！\n${shareUrl}`);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share error:', err);
      }
    }
  };

  if (loading) {
    return (
      <div className="shared-recipe-page">
        <div className="shared-recipe-page__main shared-recipe-page__loading">
          <div className="shared-recipe-page__loading-icon">🍳</div>
          <p className="shared-recipe-page__loading-text">レシピを読み込んでいます...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shared-recipe-page">
        <div className="shared-recipe-page__main shared-recipe-page__error">
          <div className="tsumugi-card">
            <div className="shared-recipe-page__error-icon">😢</div>
            <h2 className="tsumugi-card-title">レシピが見つかりません</h2>
            <p className="tsumugi-card-text">{error}</p>
            <button className="tsumugi-btn tsumugi-btn-primary" onClick={() => navigate('/')}>
              トップページへ
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shared-recipe-page">
      {/* ヘッダー */}
      <header className="shared-recipe-page__header">
        <div className="shared-recipe-page__header-content">
          <h1 className="shared-recipe-page__logo" onClick={() => navigate('/')}>
            🍳 はしもと栄養パートナー
          </h1>
          <p className="shared-recipe-page__subtitle">橋本病に配慮したレシピ</p>
        </div>
      </header>

      {/* レシピ本文 */}
      <main className="shared-recipe-page__main">
        <div className="tsumugi-card shared-recipe-page__recipe-card">
          {/* タイトル */}
          <h2 className="shared-recipe-page__title">{recipe.title}</h2>

          {/* メタ情報 */}
          <div className="shared-recipe-page__meta">
            <span className="shared-recipe-page__meta-badge shared-recipe-page__meta-badge--mint">
              👥 {recipe.servings}人前
            </span>
            <span className="shared-recipe-page__meta-badge shared-recipe-page__meta-badge--oatmeal">
              👀 {recipe.viewCount}回閲覧
            </span>
          </div>

          {/* シェアボタン */}
          <div className="shared-recipe-page__share-container">
            <button className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--mint" onClick={handleShare}>
              📤 このレシピをシェア
            </button>
          </div>

          {/* レシピ内容 - Markdownスタイリング適用 */}
          <div className="shared-recipe-page__content">
            <ReactMarkdown>{recipe.content}</ReactMarkdown>
          </div>
        </div>

        {/* アプリ誘導バナー */}
        <div className="tsumugi-card tsumugi-card--mint shared-recipe-page__cta">
          {/* つむぎイラスト */}
          <div className="shared-recipe-page__cta-icon">
            <img
              src="/tsumugi.png"
              alt="つむぎ"
            />
          </div>

          <h3 className="shared-recipe-page__cta-title">
            疲れたあなたの代わりに、<br />つむぎが考えるよ
          </h3>

          <p className="shared-recipe-page__cta-description">
            AIパートナー「つむぎ」が<br />
            <strong>あなただけのレシピ</strong>を提案します
          </p>

          {/* 特徴一覧 */}
          <div className="shared-recipe-page__cta-features">
            <div className="shared-recipe-page__feature-item">
              <strong>📸 冷蔵庫の中身から今日の夕飯</strong>
              <p className="shared-recipe-page__feature-text">
                写真を撮るだけで献立を提案
              </p>
            </div>
            <div className="shared-recipe-page__feature-item">
              <strong>🛒 特売品で節約レシピ</strong>
              <p className="shared-recipe-page__feature-text">
                お得な食材を活用
              </p>
            </div>
            <div className="shared-recipe-page__feature-item">
              <strong>🏥 もちろん橋本病に配慮</strong>
              <p className="shared-recipe-page__feature-text">
                あなたの体調に合わせた提案
              </p>
            </div>
          </div>

          {/* CTAボタン */}
          <button
            className="tsumugi-btn tsumugi-btn-primary shared-recipe-page__cta-button"
            onClick={() => navigate('/register')}
          >
            💕 無料でつむぎに相談する
          </button>

          {/* ログインリンク */}
          <p className="shared-recipe-page__cta-hint">
            すでにアカウントをお持ちの方は{' '}
            <span className="shared-recipe-page__cta-link" onClick={() => navigate('/login')}>
              ログイン
            </span>
          </p>
        </div>
      </main>

      {/* フッター */}
      <footer className="shared-recipe-page__footer">
        <h4 className="shared-recipe-page__footer-title">🍳 はしもと栄養パートナー</h4>
        <p className="shared-recipe-page__footer-description">
          橋本病の方のための栄養相談サービス<br />
          AIパートナー「つむぎ」があなたに合った食事をサポート
        </p>
        <div className="shared-recipe-page__footer-links">
          <span className="shared-recipe-page__footer-link" onClick={() => navigate('/privacy')}>
            プライバシーポリシー
          </span>
          <span className="shared-recipe-page__footer-link" onClick={() => navigate('/terms')}>
            利用規約
          </span>
        </div>
      </footer>
    </div>
  );
};

export default SharedRecipePage;
